/**
 * The record's order — `/bp/recipes/<id>/…` and `/bp/run/batches/<bn>/…` (RV11, spec/19).
 *
 * Until this existed a record could not say what waits for what, so the editor had nothing to
 * draw. There is no mock and no fallback: an outage surfaces as an error in the panel, never as
 * an invented graph. A made-up order is worse than no order — it is the one thing a GxP record
 * must not assert on its own.
 *
 * Every refusal is thrown with the SERVER'S code on it (`EDGE_CYCLE`, `EDGE_SUBJECT_UNSHARED`,
 * `EDGE_UNORDER_NEEDS_AUTHORITY` …) and whatever the server named alongside it — the ring of a
 * cycle, both operations' loop subjects. The panel renders those words rather than inventing
 * friendlier ones, so what the author reads is what the server actually said no to.
 */
import Axios from 'axios';

const bp = '/bp';

const authHeaders = () => {
  const token = localStorage.getItem('Token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/** An error that keeps the server's own code and detail. */
export class OrderRefused extends Error {
  constructor(body, status) {
    super(body?.message || 'The order could not be changed.');
    this.name = 'OrderRefused';
    this.code = body?.code || 'ORDER_ERROR';
    this.status = status;
    this.detail = body || {};
  }
}

const call = async (method, url, data, signal) => {
  try {
    const res = await Axios({ method, url, data, signal, withCredentials: true, headers: authHeaders() });
    return res?.data?.data ?? res?.data;
  } catch (e) {
    if (Axios.isCancel?.(e) || e?.name === 'CanceledError') throw e;
    throw new OrderRefused(e?.response?.data, e?.response?.status);
  }
};

/** The record's operations, its edges and the findings over them. One read for the panel. */
export const getRecordGraph = (recipeId, signal = null) =>
  call('get', `${bp}/recipes/${recipeId}/graph/`, undefined, signal);

/** Order two operations. `scope` is 'BATCH' or 'SUBJECT'; a SUBJECT edge names its subject. */
export const addOrderEdge = (recipeId, body) =>
  call('post', `${bp}/recipes/${recipeId}/edges/`, body);

/**
 * Unorder two operations. Never a delete — the row is superseded and the record keeps it, so
 * "these two were once ordered and somebody unordered them" stays answerable. Both a reason and
 * an authority are required by the server, and the panel asks for both before calling.
 */
export const unorderEdge = (recipeId, edgeId, { reason, authority }) =>
  call('delete', `${bp}/recipes/${recipeId}/edges/${edgeId}/`, { reason, authority });

/** The order a running batch is executed under — not the master's, which may have moved on. */
export const getBatchOrder = (batchNumber, signal = null) =>
  call('get', `${bp}/run/batches/${batchNumber}/order/`, undefined, signal);

/** Give a running batch the master's order. `dry_run` answers what it would cost first. */
export const adoptBatchOrder = (batchNumber, { reason, dryRun = false }) =>
  call('post', `${bp}/run/batches/${batchNumber}/order/adopt/`, { reason, dry_run: dryRun });

/** 2026-10-14 · continuous monitoring across operations (a span), authored from the designer. */
export const addSpan = (recipeId, body) =>
  call('post', `${bp}/recipes/${recipeId}/spans/`, body);

/** Remove a continuous operation from an editable record. */
export const deleteSpan = (recipeId, code) =>
  call('delete', `${bp}/recipes/${recipeId}/spans/${encodeURIComponent(code)}/`);
